import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import startVideoClockSync from './videoClockSync';

/**
 * PanoramaViewer — renders one screen's slice of a 360° equirectangular
 * panorama (image or video). The room is the inside of the sphere: wall A
 * faces north (yaw 0°), B east (90°), C south (180°), D west (270°), and
 * each wall's two screens split its sector (±22.5° with the default 45° hfov).
 *
 * yaw/hfov come from the screen code so all 8 screens together wrap the room
 * in one continuous scene.
 */

export function yawForScreenCode(code, yawOffset = 0) {
  const wallYaw = { A: 0, B: 90, C: 180, D: 270 }[code?.[0]] ?? 0;
  const position = parseInt(code?.slice(1), 10) || 1;
  // screen 1 = left half of the wall sector, screen 2 = right half
  return wallYaw + (position === 1 ? -22.5 : 22.5) + yawOffset;
}

const PanoramaViewer = ({ imageUrl, videoUrl, yaw = 0, hfov = 45, syncEpoch = null, clockOffset = 0, lite = false, playbackRate = 1 }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || (!imageUrl && !videoUrl)) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 16 / 9, 0.1, 1100);
    // Lite (play-view previews): render at a fraction of the pixels and a
    // coarser sphere — 8 concurrent panoramas must not melt one laptop
    const renderer = new THREE.WebGLRenderer({ antialias: !lite });
    renderer.setPixelRatio(lite ? 0.35 : (window.devicePixelRatio || 1));
    mount.appendChild(renderer.domElement);

    // Inside-out sphere
    const geometry = lite
      ? new THREE.SphereGeometry(500, 32, 20)
      : new THREE.SphereGeometry(500, 64, 48);
    geometry.scale(-1, 1, 1);

    let texture;
    let videoEl = null;
    if (videoUrl) {
      videoEl = document.createElement('video');
      videoEl.src = videoUrl;
      videoEl.crossOrigin = 'anonymous';
      videoEl.loop = true;
      videoEl.muted = true; // panorama audio would play 8x — one per screen
      videoEl.playsInline = true;
      videoEl.playbackRate = playbackRate;
      videoEl.play().catch(() => {});
      texture = new THREE.VideoTexture(videoEl);
      texture.colorSpace = THREE.SRGBColorSpace;
    } else if (lite) {
      // Downscale the panorama before it becomes a GPU texture: 8x 8K
      // textures ≈ 1 GB of VRAM; 2K is plenty at preview-tile size.
      // Starts as a black 1px canvas so "not loaded yet" renders dark, not white.
      const placeholder = document.createElement('canvas');
      placeholder.width = 2;
      placeholder.height = 1;
      texture = new THREE.Texture(placeholder);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = 2048;
        c.height = 1024;
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        // A NEW texture, not a mutated image: the GPU allocation is sized at
        // first upload, and re-using the placeholder texture makes three.js
        // sub-update into a 2x1 allocation (GL_INVALID_VALUE, stays black)
        const full = new THREE.CanvasTexture(c);
        full.colorSpace = THREE.SRGBColorSpace;
        mesh.material.map = full;
        mesh.material.needsUpdate = true;
        texture.dispose();
        texture = full;
      };
      img.src = imageUrl;
    } else {
      texture = new THREE.TextureLoader().load(imageUrl);
      texture.colorSpace = THREE.SRGBColorSpace;
    }

    const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ map: texture }));
    scene.add(mesh);

    // Camera direction: yaw 0 = north = -Z
    const yawRad = THREE.MathUtils.degToRad(yaw);
    camera.lookAt(Math.sin(yawRad), 0, -Math.cos(yawRad));

    const applySize = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      // hfov is what tiles the room seamlessly; three's fov is vertical
      const vfov = 2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(hfov) / 2) / camera.aspect);
      camera.fov = THREE.MathUtils.radToDeg(vfov);
      camera.updateProjectionMatrix();
    };
    applySize();
    const resizeObserver = new ResizeObserver(applySize);
    resizeObserver.observe(mount);

    // Multi-screen sync: all 8 walls hold the same video position
    const stopClockSync = videoEl && syncEpoch
      ? startVideoClockSync(videoEl, { syncEpoch, clockOffset, playbackRate })
      : null;

    // With 8 concurrent panoramas the browser can evict the oldest GL
    // contexts — recover instead of going permanently white
    const onContextLost = (e) => e.preventDefault();
    const onContextRestored = () => { texture.needsUpdate = true; };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', onContextRestored);

    let rafId;
    const animate = () => {
      rafId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      stopClockSync?.();
      resizeObserver.disconnect();
      if (videoEl) {
        videoEl.pause();
        videoEl.src = '';
      }
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', onContextRestored);
      texture.dispose();
      geometry.dispose();
      mesh.material.dispose();
      renderer.dispose();
      renderer.forceContextLoss(); // release the GL context NOW, not at GC time
      mount.removeChild(renderer.domElement);
    };
  }, [imageUrl, videoUrl, yaw, hfov, syncEpoch, clockOffset, lite]);

  return <div ref={mountRef} style={{ position: 'absolute', inset: 0, background: '#000' }} />;
};

export default PanoramaViewer;
